import {
  extractFlexibleListRows,
  numberField,
  objectField,
  stringField,
} from "./parseFields";
import type {
  PromotableCatalog,
  PromotableCatalogSummary,
  PromotableService,
  PromotableServiceFilter,
  PromotableServiceStatus,
} from "./types";

const unwrapCatalog = (payload: unknown): Record<string, unknown> | null => {
  if (payload == null || typeof payload !== "object" || Array.isArray(payload)) {
    return null;
  }
  const record = payload as Record<string, unknown>;
  if (Array.isArray(record.services) || Array.isArray(record.summary)) {
    return record;
  }
  if (
    typeof record.client === "string" ||
    typeof record.accountSlug === "string" ||
    typeof record.account_slug === "string"
  ) {
    return record;
  }
  const nested = record.data;
  if (nested != null && typeof nested === "object" && !Array.isArray(nested)) {
    return nested as Record<string, unknown>;
  }
  return record;
};

export function normalizePromotableService(row: unknown): PromotableService | null {
  if (row == null || typeof row !== "object") {
    return null;
  }
  const record = row as Record<string, unknown>;
  const name = stringField(record, "name", "serviceName", "service_name");
  const ecrRepository = stringField(
    record,
    "ecrRepository",
    "ecr_repository",
  );
  const currentTag = stringField(record, "currentTag", "current_tag");
  const valuesFile = stringField(record, "valuesFile", "values_file");
  const status = stringField(record, "status");
  const imageDigest = stringField(record, "imageDigest", "image_digest");
  const reason = stringField(record, "reason");
  if (
    !name &&
    !ecrRepository &&
    !currentTag &&
    !valuesFile &&
    !status &&
    !imageDigest
  ) {
    return null;
  }
  const id = [name, imageDigest || currentTag].filter(Boolean).join(":") || name;
  return {
    id: id || "service",
    name,
    ecrRepository,
    currentTag,
    valuesFile,
    status,
    imageDigest,
    reason,
  };
}

export function parsePromotableCatalog(
  payload: unknown,
): PromotableCatalog | null {
  const record = unwrapCatalog(payload);
  if (!record) {
    return null;
  }
  const summaryRecord = objectField(record, "summary") ?? {};
  const services = extractFlexibleListRows(
    Array.isArray(record.services) ? record.services : record,
  )
    .map(normalizePromotableService)
    .filter((row): row is PromotableService => row != null);
  const hasIdentity =
    Boolean(stringField(record, "client")) ||
    Boolean(stringField(record, "accountSlug", "account_slug")) ||
    Boolean(stringField(record, "regulator")) ||
    Array.isArray(record.services);
  if (!hasIdentity && services.length === 0) {
    return null;
  }
  const summary: PromotableCatalogSummary = {
    total: numberField(summaryRecord, "total") ?? services.length,
    approved: numberField(summaryRecord, "approved"),
    pending: numberField(summaryRecord, "pending"),
    unknown: numberField(summaryRecord, "unknown"),
  };
  return {
    client: stringField(record, "client"),
    accountSlug: stringField(record, "accountSlug", "account_slug"),
    stage: stringField(record, "stage"),
    regulator: stringField(record, "regulator"),
    generatedAt: stringField(record, "generatedAt", "generated_at"),
    summary,
    services,
  };
}

function normalizeStatusKey(status: string): string {
  return status.trim().toLowerCase().replace(/[\s-]+/g, "_");
}

export function promotableServiceStatus(
  status: string,
): PromotableServiceStatus {
  const key = normalizeStatusKey(status);
  if (key === "approved") {
    return "approved";
  }
  if (key === "pending") {
    return "pending";
  }
  return "unknown";
}

export function isPromotableServiceSelectable(service: PromotableService): boolean {
  return promotableServiceStatus(service.status) !== "approved";
}

export function filterPromotableServices(
  services: PromotableService[],
  filter: PromotableServiceFilter,
  query: string,
): PromotableService[] {
  const needle = query.trim().toLowerCase();
  return services.filter((service) => {
    if (filter !== "all" && promotableServiceStatus(service.status) !== filter) {
      return false;
    }
    if (!needle) {
      return true;
    }
    const haystack = [service.name, service.ecrRepository, service.currentTag]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(needle);
  });
}
